import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { login as apiLogin, register as apiRegister, switchMerchant as apiSwitchMerchant, getMeMerchants } from '../services/api'

interface User {
  id: string
  email: string
  role: 'admin' | 'operator' | 'auditor' | 'system_admin'
  companyName: string
  activeMerchantId: string | number
}

interface Merchant {
  id: string | number
  name: string
  status?: string
  role?: string
  updated_at?: string
}

interface AuthContextType {
  user: User | null
  merchants: Merchant[]
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, companyName: string) => Promise<void>
  logout: () => void
  isLoading: boolean
  updateProfile: (updates: Partial<User>) => void
  switchMerchant: (merchantId: string | number) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    const merchantsData = localStorage.getItem('merchants')
    if (token && userData) {
      try { setUser(JSON.parse(userData)) } catch {}
    }
    if (merchantsData) {
      try { setMerchants(JSON.parse(merchantsData)) } catch {}
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    const fn = async () => {
      if (user && merchants.length === 0) {
        try {
          const r = await getMeMerchants()
          const list = Array.isArray(r.data?.merchants) ? r.data.merchants : []
          setMerchants(list)
          localStorage.setItem('merchants', JSON.stringify(list))
        } catch {}
      }
    }
    fn()
  }, [user?.id])

  const login = async (email: string, password: string) => {
    try {
      const response = await apiLogin(email, password)
      const { token, refreshToken, user: userData, merchants: list } = response.data
      
      localStorage.setItem('token', token)
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('user', JSON.stringify(userData))
      if (Array.isArray(list)) {
        localStorage.setItem('merchants', JSON.stringify(list))
        setMerchants(list)
      } else {
        localStorage.removeItem('merchants')
        setMerchants([])
      }
      setUser(userData)
    } catch (error) {
      throw error
    }
  }

  const register = async (email: string, password: string, companyName: string) => {
    try {
      const response = await apiRegister(email, password, companyName)
      const { token, refreshToken, user: userData } = response.data
      
      localStorage.setItem('token', token)
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
      localStorage.setItem('user', JSON.stringify(userData))
      localStorage.removeItem('merchants')
      setMerchants([])
      setUser(userData)
    } catch (error) {
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('merchants')
    setUser(null)
    setMerchants([])
  }

  const updateProfile = (updates: Partial<User>) => {
    if (!user) return
    const next = { ...user, ...updates }
    localStorage.setItem('user', JSON.stringify(next))
    setUser(next)
  }

  const switchMerchant = async (merchantId: string | number) => {
    const res = await apiSwitchMerchant(merchantId)
    const { token, refreshToken } = res.data || {}
    if (token) localStorage.setItem('token', token)
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
    const target = merchants.find(m => String(m.id) === String(merchantId))
    setUser(prev => {
      if (!prev) return prev
      const next = { ...prev, activeMerchantId: merchantId, companyName: target?.name || prev.companyName }
      localStorage.setItem('user', JSON.stringify(next))
      return next
    })
  }

  return (
    <AuthContext.Provider value={{ user, merchants, login, register, logout, isLoading, updateProfile, switchMerchant }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
